import {
    BUILDER_CHART_VIEW_TYPES,
    FavoriteCoin,
    MODAL_TYPE_HANDLER,
    RANGE_TYPES,
    SegmentId,
    SegmentsData,
    ShowName,
    TABLE_INTERACTION_TYPES,
    xUserType,
} from '@/lib/types/hypertrackerTypes';
import { AUTH_TYPE } from '@/lib/types/generalTypes';
import { POSITION_DIRECTION } from '@/lib/constants/exchanges';
import { ALERT_PLANS } from '@/state/payments/paymentsTypes';

export type WalletOrderBy =
    | 'address'
    | 'totalEquity'
    | 'earliesActivityAt'
    | 'perpPnl'
    | 'perpEquity'
    | 'perpMarginEquity'
    | 'perpBias'
    | 'openValue'
    | 'sumUpnl'
    | 'closetLiqProgress'
    | 'countOpenPositions';

export type HeatmapOrderBy =
    | 'crossLeverage'
    | 'isolatedLeverage'
    | 'address'
    | 'value'
    | 'side'
    | 'size'
    | 'coin'
    | 'entryPrice'
    | 'unrealizedPnl'
    | 'funding'
    | 'liquidationPrice'
    | 'markPrice'
    | 'midPrice'
    | 'premiumPrice';

export type PositionExplorerOrderBy = HeatmapOrderBy | 'liquidationProgress';

export type BuilderUsersFilters = 'address' | 'perpEquity' | 'perpPnl' | 'builderFee' | 'volume';

export interface SegmentParams {
    segmentId: number;
}

export interface LargestPositionsParams extends SegmentParams {
    limit: number;
    offset: number;
}

export interface WalletsParams extends LargestPositionsParams {
    order: 'asc' | 'desc';
    orderBy: WalletOrderBy;
    pnlSegmentIds: number[];
    sizeSegmentIds: number[];
    hasOpenPositions: boolean | undefined;
}

export interface HeatmapRequestParams extends LargestPositionsParams {
    order: 'asc' | 'desc';
    openedWithin?: RANGE_TYPES;
    orderBy: HeatmapOrderBy;
    segmentIds: number[];
    coin: string;
}

export interface LiquidationRiskParams extends LargestPositionsParams {}

export interface HypertrackerFilters {
    largestPositions: Omit<LargestPositionsParams, 'segmentId'>;
    wallets: Omit<WalletsParams, 'segmentId'>;
    liquidationRisk: Omit<LiquidationRiskParams, 'segmentId'>;
}

export interface UsersForBuilderParams {
    limit: number;
    offset: number;
    order: 'asc' | 'desc';
    orderBy: BuilderUsersFilters;
    period: RANGE_TYPES;
}

export interface WalletRequestParams extends LargestPositionsParams {
    order: 'asc' | 'desc';
    orderBy: WalletOrderBy;
    segmentIds: number[];
    hasOpenPositions: boolean | undefined;
}

export interface WalletConnection {
    id: number;
    address: string;
    mid: number;
    emoji: string | null;
    displayName: string | null;
    showName?: ShowName | null;
    createdAt: string;
    updatedAt: string;
    xUserId: string | number | null;
    cmmId: number;
    isUnlocked: boolean;
    basedReferralRegisteredAt?: string | null;
    privateName: string | null;
    xUser?: xUserType;
    perpMonthVolume: number;
    totalEquity: number;
    vault: boolean;
    leader: string | null;
    hypertracker?: any;
    isHyperliquidReferral?: boolean;
}

export interface VaultConnection {
    apr: number;
    cmmId: number;
    description: string;
    isClosed: true;
    leader: string;
    leaderCommission: number;
    leaderFraction: number;
    mid: number;
    name: string;
    tvl: number;
    vaultAddress: string;
    xUserId: string;
}

export interface VaultInfoModalDetails {
    leaderEquity: string;
    leaderCommission: string;
    description: string;
}

export interface PositionExplorerFilters {
    coin: string;
    limit: number;
    offset: number;
    order: 'desc' | 'asc';
    orderBy: PositionExplorerOrderBy;
    segmentIds: number[];
    valueRange: string[];
}

export type LeaderboardsTableFilters = Record<LEADERBOARDS, LeaderboardSpecificTableFilters> & {
    limit: number;
};

export interface LeaderboardSpecificTableFilters {
    coin: string;
    offset: number;
    order: 'desc' | 'asc';
    rankOrder?: 'desc' | 'asc';
    orderBy: string;
    segmentIds: number[];
    valueRange: string[];
    rankBy: string;
}

export interface LiquidationHeatmapItem {
    coin: string;
    liquidationValue: number;
    priceBinIndex: number;
    priceBinStart: number;
    priceBinEnd: number;
    positionsCount: number;
    liquidation?: number;
    roundPrice?: number;
    mostImpactedSegment: any;
}

export interface BuilderByPeriod {
    address: string;
    createdAt: string;
    refCode: string | null;
    revenue: number;
    timeJoined: string | null;
    usageFee: number;
    users: number;
    volume: number;
    revenuePrev?: number;
    usersPrev?: number;
    volumePrev?: number;
    previousRank: number | null;
}

export interface Revenue {
    columns: string[];
    data: (string | number)[][];
    '24h'?: {
        uniqueUsers: number;
        revenue: number;
        timestamp: string;
    };
}

export interface BuildersByPeriod {
    builders: BuilderByPeriod[];
}

export interface TopBuildersRevenue {
    revenue: Revenue;
    topBuilders: { address: string; refCode: string }[];
}

export interface RevenueByPeriods {
    ['7d']: Revenue;
    ['24h']: Revenue;
    ['30d']: Revenue;
    all: Revenue;
}

export interface Wallet {
    address: string;
    closestLiq: {
        coin: string | null;
        progress: number | null;
    };
    cmmId: number | string | null;
    displayName: string | null;
    showName?: ShowName | null;
    earliestActivityAt: string;
    emoji: string | null;
    exposureRatio: number | null;
    favoriteCount: number;
    mid: string | number | null;
    openValue: number | null;
    perpBias: number | null;
    perpEquity: number;
    perpMarginEquity?: number;
    perpPnl: number;
    segments: SegmentId[];
    sumUpnl: number;
    totalEquity: number;
    vault: string | number | null;
    verified: boolean;
    xUserId: string | number | null;
    favorite?: {
        displayName?: string | null;
        emoji?: string | null;
        favorite?: boolean;
        featured?: boolean;
    };
    positions?: {
        coins: string[];
    };
}

export interface BuilderUser {
    address: string;
    builderFee: number | null;
    closedPnl: number;
    equity: number | null;
    perpPnl: number;
    pnl: number | null;
    volume: number;
    wallet: Wallet | null;
}

export interface Analytics {
    countUsers: number;
    revenue: number;
    volume: number;
}

export interface AnalyticsByPeriod {
    ['1h']: Analytics;
    ['24h']: Analytics;
    ['7d']: Analytics;
    ['30d']: Analytics;
    ['all']: Analytics;
}

export interface PrevAnalyticsByPeriod {
    ['24h']: Analytics;
    ['7d']: Analytics;
    ['30d']: Analytics;
}

export interface BuilderV2Personal {
    analytics: RevenueByPeriods;
    biggestDay: {
        date: string;
        revenue: number;
    };
    coinVolumeDistribution: RevenueByPeriods;
    fee: number;
    hyperliquidVolume24h: number;
    info: {
        address: string;
        code: string;
        firstActivityAt: string;
        name: string;
        url: string | null;
        xUrl: string | null;
        xUser: {
            createdAt: string;
            data: {
                description: string;
                name: string;
                profileImageUrl: string;
                publicMetrics: {
                    followersCount: number;
                    followingCount: number;
                    listedCount: number;
                    tweetCount: number;
                };
                screenName: string;
                verified: boolean;
            };
            updatedAt: string;
            xUserId: string;
        };
        xUserId: string;
    };
    numProfitableUsers: {
        ['7d']: number;
        ['24h']: number;
        ['30d']: number;
        ['all']: number;
    };
    revenueMilestones: { date: string; done: boolean; label: string; revenue: number }[];
    totalAnalytics: AnalyticsByPeriod;
    totalAnalyticsPrev: PrevAnalyticsByPeriod;
    userVolumeDistribution: RevenueByPeriods;
    users: {
        ['24h']: BuilderUser[];
        ['7d']: BuilderUser[];
        ['30d']: BuilderUser[];
        ['all']: BuilderUser[];
    };
}

export interface UsersForBuilder {
    items: BuilderUser[];
    totalCount: number;
}

export interface BuilderRangeToggle {
    [key: string]: RANGE_TYPES;
}

export interface BuilderViewToggle {
    [key: string]: BUILDER_CHART_VIEW_TYPES;
}

export interface LeaderboardItemProfile {
    emoji: string;
    displayName: string;
    showName?: ShowName | null;
    verified: boolean;
    segments?: number[];
    xUser?: xUserType;
    favorite?: {
        favorite: boolean;
        displayName: string | null;
        emoji: string | null;
        featured: boolean;
    };
}

export interface LeaderboardDataItem {
    address: string;
    age?: string;

    pnlDay?: number;
    pnlWeek?: number;
    pnlMonth?: number;
    pnlAllTime?: number;
    profile?: LeaderboardItemProfile;
    wallet?: Wallet;

    // pnl all
    totalValue?: number;
    stableValue?: number;
    topHolding?: string;

    // perp pnl
    perpEquity?: number;
    perpMarginEquity?: number;
    openValue?: number;
    openValueLong?: number;
    exposureRatio?: number;
    bias?: number;

    // avg daily perp pnl
    avgPnl?: number;
    greenDays?: number;
    volume?: number;
    highestPnl?: number;

    // referrals
    builderRewards?: number;
    claimReward?: number;
    countReferrals?: number;
    countReferrals7Days?: number;
    createdAt?: string;
    cumVolume?: number;
    refCode?: string;
    referredByAddress?: string;
    referredByCode?: string;
    timeJoined?: string;
    totalCumVolume?: number;
    totalRewards?: number;
    unclaimedRewards?: number;

    // top-claimed
    allTimePnl: number;
    monthPnl?: number;
    weekPnl?: number;
    dayPnl?: number;
    favoriteCount: number;
    perpAllTimePnl: number;
    rank: number;
    previousRank: number | null;
    totalEquity: number;
}

export interface HypertrackerLeaderboardDataResponse {
    totalCount: number;
    data: LeaderboardDataItem[];
}

export interface HypertrackerLeaderboardItemsResponse {
    totalCount: number;
    items: Holder[];
}

export interface LeaderboardReferralsJsonResponse {
    metrics: {
        refEarnings: number;
        referredUsers: number;
    };
    metrics24hAgo: {
        refEarnings: number;
        referredUsers: number;
    };
    topReferrers: ReferralsUserData[];
}

export interface HypertrackerLeaderboardPnlAllResponse {
    totalCount: number;
    sumPnl: number;
    data: LeaderboardDataItem[];
    assetsDistribution: { coin: string; value: number }[];
}

export interface HypertrackerLeaderboardsBiggestPositionsResponse {
    items: BiggestPositionsDataItem[];
    totalCount: number;
    perpTradersCount?: number;
}

export interface HypertrackerLeaderboardsTopClaimedResponse {
    connectedAccountsCountMetricsChartData: { timestamp: string; count: number }[];
    data: LeaderboardDataItem[];
    mostFollowedSocialAccount: {
        address: string;
        displayName: string | null;
        emoji: string | null;
        followersCount: number | null;
        socialName: string | null;
        xUserId: string | null;
    };
    mostFollowedWallet: {
        address: string;
        displayName: string | null;
        emoji: string | null;
        favoriteCount: number | null;
    };
    totalCount: number | null;
    userData?: TopClaimedUserData[];
}

export interface HypertrackerLeaderboardsVaultsResponse {
    closedVaultsCount: number;
    totalCount: number;
    data: VaultLeaderboardWalletData[];
    topModulesMetrics: {
        timestamp: string;
        totalTvl: number;
        totalUniqueDepositors: number;
    }[];
    userData?: VaultLeaderboardWalletData[];
}

export interface RichListAsset {
    coin: string;
    totalEquity: number;
}

export interface RichListActiveCountMetric {
    timestamp: string;
    activeCount: number;
    qualifiedWalletsCount: number;
    rankBy: string;
}

export interface RichListWalletProfile {
    emoji: string | null;
    displayName: string | null;
    showName?: ShowName | null;
    verified: boolean;
}

export interface RichListWalletData {
    address: string;
    age: string;
    totalEquity: number;
    perpEquity: number;
    spotEquity: number;
    stakingEquity: number;
    vaultEquity: number | null;
    isActive: boolean;
    rank: number;
    previousRank: number | null;
    profile: RichListWalletProfile;
}

export interface HypertrackerLeaderboardsRichListResponse {
    totalCount: number;
    top100WalletsTotalEquity: number;
    top100WalletsTop5Assets: RichListAsset[];
    activeCountMetricsChartData: RichListActiveCountMetric[];
    data: RichListWalletData[];
}

export interface MostFavoritedWalletProfile {
    emoji: string | null;
    displayName: string | null;
    showName?: ShowName | null;
    verified: boolean;
}

export interface MostFavoritedWalletData {
    address: string;
    age: string;
    totalEquity: number;
    allTimePnl: number;
    monthPnl: number | null;
    weekPnl: number | null;
    dayPnl: number | null;
    favoriteCount: number;
    favoriteCount24h: number;
    favoriteCount7d: number;
    favoriteCount30d: number;
    profile: MostFavoritedWalletProfile;
    rank: number;
    previousRank: number | null;
}

export interface HyperTrackerMostFavoriteResponse {
    totalCount: number;
    data: MostFavoritedWalletData[];
}

export interface VaultLeaderboardWalletProfile {
    displayName: string;
    showName?: ShowName | null;
    emoji: string;
    xUser: {
        xUserId: string;
        screenName: string;
        name: string;
        verified: true;
        profileImageUrl: string;
        followersCount: number;
        followingCount: number;
    };
    verified: boolean;
    favorite: { favorite: boolean; displayName: string; emoji: string; featured: boolean };
    vault: { leader: string; name: string; description: string };
    earliestActivityAt: string;
    favoriteCount: number;
    mid: number;
    cmmId: number;
    segments: number[];
    address: string;
}

export interface VaultLeaderboardWalletData {
    allTimePnl: number;
    leaderPerpAllTimePnl: number;
    allTimeAccountValue: number;
    apr: number;
    followersCount: number;
    avgFollowerPnl: number;
    address: string;
    leaderAddress: string;
    name: string;
    age: string;
    rank: number;
    previousRank: number | null;
    profile: VaultLeaderboardWalletProfile;
}

export interface HyperTrackerVaultsLeaderboardResponse {
    totalCount: number;
    data: VaultLeaderboardWalletData[];
}

export interface PositionProfile {
    address: string;
    totalEquity: number;
    perpEquity: number;
    perpMarginEquity: number;
    countOpenPositions: number;
    pnl: number;
    balance: number;
    segments: number[];
    verified: boolean;
    displayName: string | null;
    showName?: ShowName | null;
    emoji: string | null;
    xUserId: string | null;
}

export interface BiggestPositionsDataItem {
    id: string;
    side: POSITION_DIRECTION;
    address: string;
    value: number;
    size: number;
    coin: string;
    entryPrice: number;
    unrealizedPnl: number;
    funding: number;
    liquidationPrice: number;
    markPrice: number;
    midPrice: number;
    premiumPrice: number;
    crossLeverage: number;
    isolatedLeverage: number;
    closeTime: string | null;
    openTime: string;
    liquidationProgress: number;
    profile: PositionProfile;
    rank: number;
    previousRank: number | null;
    digits?: number;
}

export interface HypertrackerLeaderboardsBiggestPositions {
    perpTradersCount: number;
    totalCount: number;
    data: BiggestPositionsDataItem[];
}

export enum LEADERBOARDS {
    PNL_PERP_ONLY = 'pnl_perp_only',
    ALL_PNL = 'all_pnl',
    AVG_DAILY_PERP_PNL = 'avg_daily_perp_pnl',
    BIGGEST_POSITIONS = 'biggest_positions',
    TOP_CLAIMED = 'top_claimed',
    REFERRALS = 'referrals',
    TOP_HYPE_HOLDERS = 'top_hype_holders',
    RICH_LIST = 'rich_list',
    MOST_FAVORITED = 'most_favorited',
    VAULT_LEADERBOARDS = 'vault_leaderboards',
    BIGGEST_30D = 'biggest_30_d',
    HIGHEST_EXPOSURE = 'highest_exposure',
    FAVORITES = 'favorites',
}

export interface HolderHistoryItem {
    address: string;
    spotAmount: number;
    spotAmountUsd: number;
    stakedAmount: number;
    stakedAmountUsd: number;
    totalAmount: number;
    totalAmountUsd: number;
    createdAt: string;
}

export type HolderHistory = Record<'24h' | '7d', HolderHistoryItem>;

export interface Holder {
    address: string;
    spotAmount: number;
    spotAmountUsd: number;
    stakedAmount: number;
    stakedAmountUsd: number;
    totalAmount: number;
    totalAmountUsd: number;
    earliestActivityAt: string;
    accountValue: number;
    percentOfCirculatingSupply: number;
    history: HolderHistory;
    rank: number;
    previousRank: number | null;
    profile?: Wallet;
    wallet?: Wallet;
}

export interface StakedMetrics {
    columns: string[];
    data: number[][];
}

export interface Top100Metrics {
    columns: string[];
    data: number[][];
}

export interface HypertrackerLeaderboardsTopHypeHolders {
    circulatingSupply: number;
    countHoldersMetrics: {
        columns: string[];
        data: number[][];
    };
    stakedMetrics: StakedMetrics;
    top100Metrics: Top100Metrics;
    totalCount: number;
    data: Holder[];
    userData?: Holder[];
}

export interface MostFavoritedWallet {
    address: string;
    age: string;
    totalEquity: number;
    allTimePnl: number;
    monthPnl: number | null;
    weekPnl: number | null;
    dayPnl: number | null;
    favoriteCount: number;
    favoriteCount24h: number;
    profile: {
        displayName: string | null;
        showName?: ShowName | null;
        emoji: string | null;
        segments: number[];
        verified: boolean;
    };
}

export interface LeaderboardsMostFavouritedJsonResponse {
    data: MostFavoritedWalletData[];
    favoritesMetricsChartData: {
        timestamp: string;
        uniqueCount: number;
    }[];
    mostFavoritedWallet: MostFavoritedWallet;
    totalCount: number;
    totalFavorites: number;
    userData?: MostFavoritedWalletData[];
}

export interface LeaderboardsTopHypeHoldersJsonResponse {
    circulatingSupply: number;
    countHoldersMetrics: {
        columns: string[];
        data: number[][];
    };
    stakedMetrics: StakedMetrics;
    top100Metrics: Top100Metrics;
    totalCount: number;
    holders: Holder[];
}

export interface HypertrackerLeaderboardsTopHypeHoldersResponse extends Omit<
    HypertrackerLeaderboardsTopHypeHolders,
    'data' | 'userData' | 'countHoldersMetrics'
> {
    holders: Holder[];
    countHoldersMetrics: {
        columns: string[];
        data: number[][];
    };
}

export interface HypertrackerLeaderboardsReferrals {
    metrics?: {
        referredUsers: number;
        refEarnings: number;
    };
    metrics24hAgo: {
        referredUsers: number;
        refEarnings: number;
    };
    data: ReferralsUserData[];
    userData?: ReferralsUserData[];
    totalCount?: number;
}

export interface TopClaimedUserData {
    address: string;
    age: string;
    allTimePnl: number;
    favoriteCount: number | null;
    perpAllTimePnl: number | null;
    perpEquity: number | null;
    perpMarginEquity: number;
    rank: number;
    previousRank: number | null;
    totalEquity: number | null;
    profile: LeaderboardItemProfile;
}

export interface HypeHoldersUserData {
    accountValue: number;
    address: string;
    earliestActivityAt: string;
    history: any;
    percentOfCirculatingSupply: number;
    rank: number;
    previousRank: number | null;
    spotAmount: number;
    spotAmountUsd: number;
    stakedAmount: number;
    stakedAmountUsd: number;
    totalAmount: number;
    totalAmountUsd: number;
    wallet?: Wallet;
    profile?: Wallet;
}

export interface ReferralsUserData {
    address: string;
    builderRewards: number;
    claimReward: number;
    countReferrals: number;
    countReferrals24Hours: number;
    countReferrals7Days: number;
    countReferrals30Days: number;
    createdAt: string;
    cumVolume: number;
    rank: number;
    previousRank: number | null;
    refCode: string;
    referredByAddress: string;
    referredByCode: string;
    timeJoined: string;
    totalCumVolume: number;
    totalRewards: number;
    unclaimedRewards: number;
    wallet?: Wallet;
    profile?: Wallet;
}

export type HypertrackerLeaderboards = Record<
    LEADERBOARDS,
    | HypertrackerLeaderboardDataResponse
    | HypertrackerLeaderboardsBiggestPositions
    | HypertrackerLeaderboardsTopHypeHolders
    | HypertrackerLeaderboardsReferrals
    | HypertrackerLeaderboardsTopClaimedResponse
>;

export interface ModalWindow {
    modalType?: MODAL_TYPE_HANDLER | null;
    isTwitterData?: boolean;
    xUser?: xUserType | null;
    benefitModalType?: TABLE_INTERACTION_TYPES | null;
    authorizationModalType?: AUTH_TYPE | null;
    wallet?: string;
    positionId?: string;
    editConnection?: any;
    twitterConnectUrl?: string;
    editDisplayedNameForFavorites?: any;
    vaultInfoModalDetails?: VaultInfoModalDetails | null;
    /** AlphaFeed trading setup: wallet to open straight into the approval steps. */
    address?: string;
    /** AlphaFeed onboarding: side of the feed card button that opened it, preselected on Learn. */
    onboardingSide?: 'long' | 'short';

    [key: string]: any;
}

export interface UpdateJsonStatus {
    lastUpdate: string;
    nextUpdate: string;
    nextUpdateThreshold: string;
}

export interface PerformanceData {
    pnlDay: LeaderboardDataItem[];
    pnlWeek: LeaderboardDataItem[];
    pnlMonth: LeaderboardDataItem[];
}

export interface BehaviouralData {
    biggestPositions: BiggestPositionsDataItem[];
    topHypeHolders: LeaderboardDataItem[] | Holder[];
    richList: RichListWalletData[] | LeaderboardDataItem[];
}

export interface SocialData {
    mostFavorited: MostFavoritedWalletData[];
    topBuilders: {
        rank: number;
        address: string;
        value: number;
    }[];
    topClaimed: LeaderboardDataItem[];
}

export interface MeType {
    accountType: ALERT_PLANS;
    id: number;
    username: string;
    accountTypeSource: string;
}

export interface TelegramConnectionHT {
    firstName: string;
    lastName: string;
    username: string;
}

export interface ActiveSubscription {
    applicationCode: string;
    expiresAt: number;
    productCode: ALERT_PLANS;
    subscriptionPlanDuration: 'string';
    subscriptionStatus: string;
    subscriptionUuid: string;
}

export interface ActiveSubscriptionResponse {
    subscriptions: ActiveSubscription[];
}

export interface HypertrackerState {
    segmentData: SegmentsData | null;
    largestPositions: Record<number, any>;
    liquidationRisk: Record<number, any>;
    filters: HypertrackerFilters;
    modalWindow: ModalWindow;
    isLoadingDisplayX: boolean;
    heatmapSelectedRow: string | null;
    heatmapSelectedField: number | null;
    tempKeyData: any;
    connections: WalletConnection[];
    vaults: VaultConnection[];
    perpsCurrentAsset: string;
    perpsPositionsToExplore: Array<string | number>;
    positionExplorerFilters: PositionExplorerFilters;
    liquidationHeatmapData: Record<string, LiquidationHeatmapItem[]>;
    buildersDailyAnalytics: Revenue | null;
    buildersAllTimeAnalytics: Revenue | null;
    buildersAllTime: BuildersByPeriod | null;
    builders24h: BuildersByPeriod | null;
    marketRadarActiveTab: number;
    marketRadarSelectedColor: 'colored' | 'mono';
    marketRadarLimitOrderType: 'increase' | 'decrease' | 'all';
    topBuildersRevenue: TopBuildersRevenue | null;
    builderMainChartView: BuilderViewToggle | null;
    buildersTableRange: RANGE_TYPES;
    personalBuilderRange: BuilderRangeToggle | null;
    leaderboardsTableFilters: LeaderboardsTableFilters;
    buildersForSearch: any[];
    positionRecencyToggle: RANGE_TYPES;
    leaderboards: HypertrackerLeaderboards | null;
    leaderboardsPerformanceData: PerformanceData | null;
    leaderboardsBehaviouralData: BehaviouralData | null;
    leaderboardsSocialData: SocialData | null;
    me: MeType | null;
    telegramConnectionHT: TelegramConnectionHT | null;
    marketRadarSelectedCoin: string;
    marketRadarSelectedCandle: string;
    favoriteCoinsList: FavoriteCoin[];
    perpsPositionAge: RANGE_TYPES;
    /** Claimed wallet the user picked for AlphaFeed trading; revalidated on page load. */
    alphafeedSelectedWallet: string | null;
}

export interface ClosedTrade {
    address: string;
    avgEntry: number;
    avgExit: number | null;
    closeTime: string;
    coin: string;
    countFills: number | null;
    duration: number | null;
    fee: number | null;
    feeUsd: number | null;
    fundingUsd: number | null;
    hash: string;
    id: number;
    openTime: string;
    partial: boolean;
    realizedPnlUsd: number;
    side: string;
    totalSize: number | null;
    totalUsd: number;
    profile?: VaultLeaderboardWalletProfile;
    result?: 'win' | 'loss';
    journeyHigh?: number | null;
    journeyHighAt?: string | null;
    journeyLow?: number | null;
    journeyLowAt?: string | null;
    maePrice?: number | null;
    maePercent?: number | null;
    mfePrice?: number | null;
    mfePercent?: number | null;
}

export interface ClosedTradesResponse {
    totalCount: number;
    items: ClosedTrade[];
}

export enum CLOSED_TRADES_SUMMARY_RANGE {
    LAST_30_DAYS = '30d',
    LAST_90_DAYS = '90d',
    LAST_180_DAYS = '180d',
    LAST_365_DAYS = '365d',
    ALL_TIME = 'all',
    LAST_50_TRADES = 'last50',
}

export enum CLOSED_TRADES_SUMMARY_RANGE_TITLE_KEYS {
    LAST_30_DAYS = 'summary_ranges.last_30_days',
    LAST_90_DAYS = 'summary_ranges.last_90_days',
    LAST_180_DAYS = 'summary_ranges.last_180_days',
    LAST_365_DAYS = 'summary_ranges.last_365_days',
    ALL_TIME = 'summary_ranges.all_time',
    LAST_50_TRADES = 'summary_ranges.last_50_trades',
}

export interface ClosedTradesSummaryMetrics {
    totalTrades: number;
    wins: number;
    losses: number;
    winRate: number;
    longTrades: number;
    shortTrades: number;
    avgDuration: number;
    medianDuration: number;
    totalWinningPnl: number;
    totalLosingPnl: number;
    netPnl: number;
    avgGain: number | null;
    avgLoss: number | null;
    medianGain: number | null;
    medianLoss: number | null;
    profitFactor: number | null;
    payoffRatio: number | null;
    expectancy: number | null;
    expectancyPct: number | null;
    totalFeesPaid: number;
    avgTradeSize: number;
    medianSizeUsd: number;
    totalVolumeUsd: number;
}

export interface ClosedTradesCoinSummary extends ClosedTradesSummaryMetrics {
    coin: string;
    volumeSharePct: number;
}

export interface ClosedTradesSummary {
    address: string;
    range: string;
    updatedAt: string;
    summary: ClosedTradesSummaryMetrics;
    coins: ClosedTradesCoinSummary[];
}

export interface ClosedTradesCandle {
    dateOpen: string;
    dateClose: string;
    open: number;
    high: number;
    low: number;
    close: number;
    dayVolume: number;
    funding: number;
    volumeBase?: number;
    volumeQuote?: number;
    tradeCount?: number;
    synthetic?: boolean;
}

export interface ClosedTradesCandlesHistoryResponse {
    history: ClosedTradesCandle[];
}

export interface ClosedTradesBatchedFill {
    address: string;
    builder: string;
    builderFee: number;
    closedPnl: number;
    coin: string;
    crossed: true;
    dir: string;
    fee: number;
    feeToken: string;
    fillCount: number;
    fillType: string;
    fullCoinName: string;
    hash: string;
    oid: number;
    px: number;
    side: string;
    startPosition: number;
    sz: number;
    time: string;
    notionalValue?: number;
    candleIndex?: number;
}

export interface ClosedTradesBatchedFillsResponse {
    cursor: string | null;
    fills: ClosedTradesBatchedFill[];
}
